import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

// CampaignDto solo acepta estos campos (language es opcional).
const buildCampaignBody = ({ topic, subject, body, language }) => ({
  topic,
  subject,
  body,
  ...(language ? { language } : {}),
});

// ── Thunks ────────────────────────────────────────────────────────────────────

// Público: { topics: [{ key, label }] } (solo temas con suscriptores activos).
export const fetchTopics = createAsyncThunk(
  "newsletter/fetchTopics",
  withApi(async () => {
    const data = await apiRequest("/newsletter/topics");
    return data?.topics || [];
  })
);

// Rol marketing: CampaignSummary[] (mismo formato que el antiguo /campaigns/stats + status y conteos de envío).
export const fetchCampaignStats = createAsyncThunk(
  "newsletter/fetchCampaignStats",
  withApi(async (_, { token }) => {
    const data = await apiRequest("/newsletter/campaigns", { token });
    return Array.isArray(data) ? data : [];
  })
);

// Envío masivo: responde enseguida y el envío sigue en segundo plano.
export const sendNewsletter = createAsyncThunk(
  "newsletter/sendNewsletter",
  withApi(async (campaign, { token }) => {
    const { data, message } = await apiRequest("/newsletter/campaigns", {
      method: "POST",
      body: buildCampaignBody(campaign),
      token,
      withMessage: true,
    });
    return { campaign: data, message };
  })
);

// Envía la campaña solo al correo interno de quien la prueba. Devuelve { to }.
export const sendTestNewsletter = createAsyncThunk(
  "newsletter/sendTestNewsletter",
  withApi(async (campaign, { token }) => {
    const { data, message } = await apiRequest("/newsletter/campaigns/test", {
      method: "POST",
      body: buildCampaignBody(campaign),
      token,
      withMessage: true,
    });
    return { to: data?.to, message };
  })
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const newsletterSlice = createSlice({
  name: "newsletter",
  initialState: {
    topics: [],
    topicsStatus: "idle",
    topicsError: null,
    sendStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    sendError: null,
    testStatus: "idle",
    testError: null,
    campaignStats: [],
    campaignStatsStatus: "idle",
    campaignStatsError: null,
  },
  reducers: {
    resetSendStatus(state) {
      state.sendStatus = "idle";
      state.sendError = null;
      state.testStatus = "idle";
      state.testError = null;
    },
    resetCampaignStats(state) {
      state.campaignStats = [];
      state.campaignStatsStatus = "idle";
      state.campaignStatsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopics.pending, (state) => {
        state.topicsStatus = "loading";
        state.topicsError = null;
      })
      .addCase(fetchTopics.fulfilled, (state, action) => {
        state.topicsStatus = "succeeded";
        state.topics = action.payload;
        state.topicsError = null;
      })
      .addCase(fetchTopics.rejected, (state, action) => {
        state.topicsStatus = "failed";
        state.topicsError = action.payload ?? "Error al cargar topics";
      })
      .addCase(sendNewsletter.pending, (state) => {
        state.sendStatus = "loading";
        state.sendError = null;
      })
      .addCase(sendNewsletter.fulfilled, (state) => {
        state.sendStatus = "succeeded";
        state.sendError = null;
      })
      .addCase(sendNewsletter.rejected, (state, action) => {
        state.sendStatus = "failed";
        state.sendError = action.payload ?? "Error desconocido";
      })
      .addCase(sendTestNewsletter.pending, (state) => {
        state.testStatus = "loading";
        state.testError = null;
      })
      .addCase(sendTestNewsletter.fulfilled, (state) => {
        state.testStatus = "succeeded";
        state.testError = null;
      })
      .addCase(sendTestNewsletter.rejected, (state, action) => {
        state.testStatus = "failed";
        state.testError = action.payload ?? "Error desconocido";
      })
      .addCase(fetchCampaignStats.pending, (state) => {
        state.campaignStatsStatus = "loading";
        state.campaignStatsError = null;
      })
      .addCase(fetchCampaignStats.fulfilled, (state, action) => {
        state.campaignStatsStatus = "succeeded";
        state.campaignStats = action.payload;
      })
      .addCase(fetchCampaignStats.rejected, (state, action) => {
        state.campaignStatsStatus = "failed";
        state.campaignStatsError = action.payload ?? "Error al cargar estadísticas";
      });
  },
});

export const { resetSendStatus, resetCampaignStats } = newsletterSlice.actions;
export default newsletterSlice.reducer;
